// assets/smart-search-ai.js - دعم كل الحالات

console.log('📦 Smart Search AI - Full Support');

class SmartSearchAI {
  constructor() {
    this.searchIndex = [];
    this.isInitialized = false;
    this.cache = new Map();
    this.tokenizedIndex = new Map();
    this.productCache = new Map();
    this.searchHistory = [];
    this.maxHistory = 50;
    this.initializationPromise = null;
    this.totalProducts = 0;
    this.isLoading = false;
    
    // SKU Index للبحث السريع
    this.skuIndex = new Map();
    this.allProducts = [];
    this.productsLoaded = false;
  }

  async initialize() {
    if (this.isInitialized) {
      console.log('✅ Smart Search already initialized');
      return;
    }
    if (this.initializationPromise) return this.initializationPromise;
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.initializationPromise = this._initialize();
    return this.initializationPromise;
  }

  async _initialize() {
    try {
      console.log('🚀 Initializing Smart Search AI...');
      console.log('📊 Loading products from your store...');
      
      await this.loadAllProductsDirect();
      await this.buildSearchIndexChunked(this.allProducts);
      
      this.isInitialized = true;
      this.isLoading = false;
      console.log(`✅ Smart Search ready with ${this.totalProducts} products!`);
      
    } catch (error) {
      console.error('❌ Failed to initialize search:', error);
      this.initializationPromise = null;
      this.isLoading = false;
      setTimeout(() => this.initialize(), 5000);
    }
  }

  async loadAllProductsDirect() {
    try {
      console.log('📥 Loading all products directly...');
      
      let allProducts = [];
      let page = 1;
      const limit = 250;
      let hasMore = true;
      let totalLoaded = 0;

      while (hasMore) {
        try {
          console.log(`📥 Loading page ${page}...`);
          const response = await fetch(`/collections/all/products.json?page=${page}&limit=${limit}`);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          
          if (!data.products || data.products.length === 0) {
            hasMore = false;
            break;
          }
          
          allProducts = allProducts.concat(data.products);
          totalLoaded += data.products.length;
          console.log(`📥 Loaded ${totalLoaded} products so far...`);
          
          if (data.products.length < limit) {
            hasMore = false;
          } else {
            page++;
          }
          
          if (page > 50) {
            console.warn('⚠️ Reached page limit, stopping...');
            hasMore = false;
          }
          
        } catch (error) {
          console.error('❌ Error loading products page:', error);
          hasMore = false;
        }
      }
      
      this.allProducts = allProducts;
      this.totalProducts = allProducts.length;
      this.productsLoaded = true;
      
      // بناء SKU Index مع دعم كل الحالات
      this.buildSkuIndex(allProducts);
      
      console.log(`✅ Loaded ${this.totalProducts} products total`);
      
    } catch (error) {
      console.error('Load error:', error);
      throw error;
    }
  }

  // بناء SKU Index مع دعم كل الحالات
  buildSkuIndex(products) {
    console.log('📊 Building SKU index...');
    let count = 0;
    
    for (const product of products) {
      if (product.variants) {
        for (const variant of product.variants) {
          if (variant.sku) {
            const sku = variant.sku;
            const cleanSku = sku.toLowerCase().trim();
            
            // 🔥 1. SKU الأصلي
            this.skuIndex.set(cleanSku, { product, variant });
            count++;
            
            // 🔥 2. بدون مسافات وشرطات (FC5, KVA2, OC3)
            const noSpace = cleanSku.replace(/[-_\s]/g, '');
            if (noSpace !== cleanSku && noSpace.length > 0) {
              if (!this.skuIndex.has(noSpace)) {
                this.skuIndex.set(noSpace, { product, variant });
              }
            }
            
            // 🔥 3. بمسافات (FC 5, KVA 2, OC 3)
            const withSpace = cleanSku.replace(/([a-z])(\d)/g, '$1 $2').replace(/(\d)([a-z])/g, '$1 $2');
            if (withSpace !== cleanSku && withSpace.length > 0) {
              if (!this.skuIndex.has(withSpace)) {
                this.skuIndex.set(withSpace, { product, variant });
              }
            }
            
            // 🔥 4. بشرطة (FC-5, KVA-2, OC-3)
            const withDash = cleanSku.replace(/([a-z])(\d)/g, '$1-$2').replace(/(\d)([a-z])/g, '$1-$2');
            if (withDash !== cleanSku && withDash.length > 0) {
              if (!this.skuIndex.has(withDash)) {
                this.skuIndex.set(withDash, { product, variant });
              }
            }
            
            // 🔥 5. أجزاء من SKU (FC, 5, KVA, 2, OC, 3)
            const parts = cleanSku.split(/[-_\s]/);
            for (const part of parts) {
              if (part.length > 0 && !this.skuIndex.has(part)) {
                this.skuIndex.set(part, { product, variant });
              }
            }
            
            // 🔥 6. دعم المنتجات القصيرة
            const normalized = cleanSku.replace(/[-_\s]/g, '');
            if (normalized.length <= 8) {
              for (let i = 0; i < normalized.length - 1; i++) {
                for (let j = i + 2; j <= i + 4 && j <= normalized.length; j++) {
                  const sub = normalized.substring(i, j);
                  if (!this.skuIndex.has(sub)) {
                    this.skuIndex.set(sub, { product, variant });
                  }
                }
              }
            }
            
            // 🔥 7. دعم الحروف مع الأرقام (FC5, KVA2)
            const letters = cleanSku.match(/[a-z]+/g);
            const numbers = cleanSku.match(/\d+/g);
            if (letters && numbers) {
              for (const letter of letters) {
                for (const number of numbers) {
                  const combo = letter + number;
                  if (!this.skuIndex.has(combo)) {
                    this.skuIndex.set(combo, { product, variant });
                  }
                }
              }
            }
          }
        }
      }
    }
    
    console.log(`✅ SKU index built with ${this.skuIndex.size} entries`);
  }

  async buildSearchIndexChunked(products) {
    console.log('📊 Building search index in chunks...');
    let processedCount = 0;
    const chunkSize = 50;
    
    this.tokenizedIndex = new Map();
    this.productCache = new Map();
    
    for (let i = 0; i < products.length; i += chunkSize) {
      const chunk = products.slice(i, i + chunkSize);
      
      await new Promise(resolve => {
        setTimeout(() => {
          chunk.forEach(product => {
            this.productCache.set(product.id, product);
            const tokens = this.tokenizeProduct(product);
            
            product.variants.forEach(variant => {
              const variantTokens = this.tokenizeVariant(variant);
              const allTokens = [...tokens, ...variantTokens];
              
              allTokens.forEach(token => {
                if (!this.tokenizedIndex.has(token)) {
                  this.tokenizedIndex.set(token, []);
                }
                
                const existing = this.tokenizedIndex.get(token);
                const isDuplicate = existing.some(item => 
                  item.product.id === product.id && 
                  item.variant.id === variant.id
                );
                
                if (!isDuplicate) {
                  existing.push({
                    product: product,
                    variant: variant,
                    token: token,
                    score: this.calculateTokenScore(token, variant)
                  });
                }
              });
            });
          });
          
          processedCount += chunk.length;
          if (processedCount % 100 === 0) {
            console.log(`📊 Processed ${processedCount} products...`);
          }
          resolve();
        }, 10);
      });
    }
    
    console.log(`✅ Index built with ${this.tokenizedIndex.size} unique tokens`);
  }

  tokenizeProduct(product) {
    const tokens = new Set();
    const cleanText = (text) => text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ');
    
    if (product.title) {
      cleanText(product.title).split(/\s+/).filter(t => t.length > 1).forEach(t => tokens.add(t));
    }
    if (product.product_type) {
      cleanText(product.product_type).split(/\s+/).filter(t => t.length > 1).forEach(t => tokens.add(t));
    }
    if (product.vendor) {
      cleanText(product.vendor).split(/\s+/).filter(t => t.length > 1).forEach(t => tokens.add(t));
    }
    if (product.tags && Array.isArray(product.tags)) {
      product.tags.forEach(tag => {
        cleanText(tag).split(/\s+/).filter(t => t.length > 1).forEach(t => tokens.add(t));
      });
    }
    
    return Array.from(tokens);
  }

  tokenizeVariant(variant) {
    const tokens = new Set();
    
    if (variant.sku && variant.sku.trim()) {
      const sku = variant.sku.toLowerCase().trim();
      
      // 🔥 دعم كل الحالات
      tokens.add(sku);
      
      // بدون مسافات وشرطات
      const noSpace = sku.replace(/[-_\s]/g, '');
      if (noSpace !== sku) {
        tokens.add(noSpace);
      }
      
      // بمسافات
      const withSpace = sku.replace(/([a-z])(\d)/g, '$1 $2').replace(/(\d)([a-z])/g, '$1 $2');
      if (withSpace !== sku) {
        tokens.add(withSpace);
      }
      
      // بشرطة
      const withDash = sku.replace(/([a-z])(\d)/g, '$1-$2').replace(/(\d)([a-z])/g, '$1-$2');
      if (withDash !== sku) {
        tokens.add(withDash);
      }
      
      // أجزاء من SKU
      const parts = sku.split(/[-_\s]/);
      for (const part of parts) {
        if (part.length > 0) {
          tokens.add(part);
          
          const letters = part.match(/[a-z]+/g);
          const numbers = part.match(/\d+/g);
          
          if (letters) {
            for (const l of letters) {
              if (l.length > 0) tokens.add(l);
            }
          }
          if (numbers) {
            for (const n of numbers) {
              if (n.length > 0) tokens.add(n);
            }
          }
        }
      }
      
      // دعم المنتجات القصيرة
      const normalized = sku.replace(/[-_\s]/g, '');
      if (normalized.length <= 8) {
        for (let i = 0; i < normalized.length - 1; i++) {
          for (let j = i + 2; j <= i + 4 && j <= normalized.length; j++) {
            tokens.add(normalized.substring(i, j));
          }
        }
      }
    }
    
    if (variant.title && variant.title.trim()) {
      variant.title.toLowerCase().split(/\s+/).filter(t => t.length > 1).forEach(t => tokens.add(t));
    }
    
    if (variant.option1) tokens.add(variant.option1.toLowerCase());
    if (variant.option2) tokens.add(variant.option2.toLowerCase());
    if (variant.option3) tokens.add(variant.option3.toLowerCase());
    
    return Array.from(tokens);
  }

  tokenizeSKU(sku) {
    const tokens = new Set();
    const cleanSKU = sku.toLowerCase().trim();
    
    tokens.add(cleanSKU);
    
    const patterns = [
      { regex: /^[a-z]{2,4}/, label: 'prefix' },
      { regex: /[a-z]{2,4}$/, label: 'suffix' },
      { regex: /\d+/, label: 'numbers' },
      { regex: /[a-z]+/g, label: 'letters' }
    ];
    
    patterns.forEach(({ regex }) => {
      const matches = cleanSKU.match(regex);
      if (matches) {
        matches.forEach(match => {
          if (match.length > 1) tokens.add(match);
        });
      }
    });
    
    const variations = [
      cleanSKU.replace(/-/g, ''),
      cleanSKU.replace(/_/g, ''),
      cleanSKU.replace(/[^a-z0-9]/g, '')
    ];
    variations.forEach(v => {
      if (v !== cleanSKU && v.length > 0) tokens.add(v);
    });
    
    return Array.from(tokens);
  }

  calculateTokenScore(token, variant) {
    let score = 0;
    const cleanToken = token.toLowerCase();
    
    if (variant.sku && variant.sku.toLowerCase() === cleanToken) {
      score += 100;
    }
    
    if (variant.sku && variant.sku.toLowerCase().includes(cleanToken)) {
      score += 50;
      if (variant.sku.toLowerCase().startsWith(cleanToken)) {
        score += 25;
      }
    }
    
    if (cleanToken.length > 3) {
      score += cleanToken.length * 0.5;
    }
    
    if (/[0-9]/.test(cleanToken) && /[a-z]/.test(cleanToken)) {
      score += 10;
    }
    
    return Math.round(score);
  }

  async search(query, options = {}) {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    if (!query || query.trim().length === 0) {
      return [];
    }
    
    const {
      fuzzyThreshold = 2,
      maxResults = 50,
      minScore = 5,
      boostExact = true
    } = options;
    
    const cleanQuery = query.toLowerCase().trim();
    
    // 🔥 توليد كل الـ variations الممكنة
    const searchVariations = this.getSearchVariations(cleanQuery);
    
    const results = [];
    const seenProducts = new Map();
    
    const cacheKey = cleanQuery;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // 🔥 البحث في كل الـ variations
    for (const searchQuery of searchVariations) {
      // 1. البحث المباشر في SKU Index
      const skuMatch = this.skuIndex.get(searchQuery);
      if (skuMatch) {
        const product = skuMatch.product;
        const variant = skuMatch.variant;
        const key = `${product.id}-${variant.id}`;
        
        if (!seenProducts.has(key)) {
          seenProducts.set(key, {
            product: product,
            variant: variant,
            score: 999,
            matchedToken: searchQuery,
            matchType: 'exact',
            productScore: 0
          });
        }
      }

      // 2. البحث في كل المنتجات
      const products = this.allProducts;
      if (products.length > 0) {
        for (const product of products) {
          for (const variant of product.variants) {
            const sku = variant.sku ? variant.sku.toLowerCase() : '';
            const title = product.title ? product.title.toLowerCase() : '';
            const key = `${product.id}-${variant.id}`;
            
            if (seenProducts.has(key)) continue;
            
            let score = 0;
            let matchType = 'partial';
            
            // SKU يحتوي الكلمة
            if (sku.includes(searchQuery) || sku.replace(/[-_\s]/g, '').includes(searchQuery.replace(/[-_\s]/g, ''))) {
              score = 80;
              matchType = 'contains';
            }
            // SKU يبدأ بالكلمة
            else if (sku.startsWith(searchQuery) || sku.replace(/[-_\s]/g, '').startsWith(searchQuery.replace(/[-_\s]/g, ''))) {
              score = 85;
              matchType = 'prefix';
            }
            // العنوان يحتوي الكلمة
            else if (title.includes(searchQuery)) {
              score = 60;
              matchType = 'title';
            }
            // العنوان يبدأ بالكلمة
            else if (title.startsWith(searchQuery)) {
              score = 65;
              matchType = 'title';
            }
            // تشابه مع SKU
            else if (searchQuery.length >= 3) {
              const cleanSku = sku.replace(/[-_\s]/g, '');
              const cleanSearch = searchQuery.replace(/[-_\s]/g, '');
              const similarity = this.getSimilarity(cleanSearch, cleanSku);
              if (similarity > 0.4) {
                score = Math.round(similarity * 70);
                matchType = 'fuzzy';
              }
            }
            
            // زيادة الوزن للاستعلامات القصيرة
            if (searchQuery.length <= 4 && score > 0) {
              score += 20;
            }
            
            if (score >= minScore) {
              seenProducts.set(key, {
                product: product,
                variant: variant,
                score: score,
                matchedToken: searchQuery,
                matchType: matchType,
                productScore: 0
              });
            }
          }
        }
      }
    }

    // 3. البحث في الـ tokenized index
    if (this.tokenizedIndex.size > 0) {
      const matchingTokens = this.findMatchingTokens(cleanQuery, fuzzyThreshold);
      
      if (matchingTokens.length === 0) {
        const fuzzyMatches = this.findFuzzyMatches(cleanQuery, fuzzyThreshold);
        matchingTokens.push(...fuzzyMatches);
      }
      
      matchingTokens.forEach(token => {
        const items = this.tokenizedIndex.get(token) || [];
        items.forEach(item => {
          const productId = item.product.id;
          const variantId = item.variant.id;
          const key = `${productId}-${variantId}`;
          
          if (seenProducts.has(key)) return;
          
          const score = this.calculateSearchScore(item, cleanQuery, token);
          
          if (score >= minScore) {
            seenProducts.set(key, {
              product: item.product,
              variant: item.variant,
              score: score,
              matchedToken: token,
              matchType: this.getMatchType(token, cleanQuery),
              productScore: this.calculateProductScore(item.product, cleanQuery)
            });
          }
        });
      });
    }

    const finalResults = Array.from(seenProducts.values());
    
    finalResults.sort((a, b) => {
      const scoreA = a.score + (a.productScore || 0);
      const scoreB = b.score + (b.productScore || 0);
      return scoreB - scoreA;
    });
    
    if (boostExact) {
      finalResults.forEach(result => {
        const sku = result.variant.sku ? result.variant.sku.toLowerCase().replace(/[-_\s]/g, '') : '';
        if (sku === cleanQuery.replace(/[-_\s]/g, '')) {
          result.score += 50;
        }
      });
      finalResults.sort((a, b) => b.score - a.score);
    }
    
    const limitedResults = finalResults.slice(0, maxResults);
    this.cache.set(cacheKey, limitedResults);
    this.updateSearchHistory(cleanQuery, limitedResults);
    
    return limitedResults;
  }

  // 🔥 توليد كل الـ variations للبحث
  getSearchVariations(query) {
    const variations = new Set();
    
    // 1. الاستعلام الأصلي
    variations.add(query);
    
    // 2. بدون مسافات وشرطات
    const noSpace = query.replace(/[-_\s]/g, '');
    if (noSpace !== query && noSpace.length > 0) {
      variations.add(noSpace);
    }
    
    // 3. بمسافات
    const withSpace = query.replace(/([a-z])(\d)/g, '$1 $2').replace(/(\d)([a-z])/g, '$1 $2');
    if (withSpace !== query && withSpace.length > 0) {
      variations.add(withSpace);
    }
    
    // 4. بشرطة
    const withDash = query.replace(/([a-z])(\d)/g, '$1-$2').replace(/(\d)([a-z])/g, '$1-$2');
    if (withDash !== query && withDash.length > 0) {
      variations.add(withDash);
    }
    
    // 5. أجزاء من الاستعلام
    const parts = query.split(/[-_\s]/);
    for (const part of parts) {
      if (part.length > 0) {
        variations.add(part);
      }
    }
    
    // 6. دمج الحروف مع الأرقام
    const letters = query.match(/[a-z]+/g);
    const numbers = query.match(/\d+/g);
    if (letters && numbers) {
      for (const letter of letters) {
        for (const number of numbers) {
          const combo = letter + number;
          if (combo !== query) {
            variations.add(combo);
          }
        }
      }
    }
    
    return Array.from(variations);
  }

  getSimilarity(str1, str2) {
    if (str1 === str2) return 1;
    if (str1.length === 0 || str2.length === 0) return 0;
    
    const maxLen = Math.max(str1.length, str2.length);
    if (Math.abs(str1.length - str2.length) > 3) return 0;
    
    const m = str1.length;
    const n = str2.length;
    let prev = new Array(n + 1);
    let curr = new Array(n + 1);
    
    for (let j = 0; j <= n; j++) prev[j] = j;
    
    for (let i = 1; i <= m; i++) {
      curr[0] = i;
      for (let j = 1; j <= n; j++) {
        if (str1[i-1] === str2[j-1]) {
          curr[j] = prev[j-1];
        } else {
          curr[j] = Math.min(prev[j-1] + 1, curr[j-1] + 1, prev[j] + 1);
        }
      }
      [prev, curr] = [curr, prev];
    }
    
    const distance = prev[n];
    const maxLen2 = Math.max(m, n);
    return 1 - (distance / maxLen2);
  }

  findMatchingTokens(query, fuzzyThreshold) {
    const matches = new Set();
    const tokenKeys = Array.from(this.tokenizedIndex.keys());
    
    if (tokenKeys.includes(query)) {
      matches.add(query);
    }
    
    tokenKeys.forEach(token => {
      if (token.startsWith(query) && token !== query) {
        matches.add(token);
      }
      if (token.endsWith(query) && token !== query) {
        matches.add(token);
      }
    });
    
    if (query.length >= 3) {
      tokenKeys.forEach(token => {
        if (token.includes(query) && !matches.has(token)) {
          matches.add(token);
        }
      });
    }
    
    if (query.length > 2) {
      tokenKeys.forEach(token => {
        if (token.length > query.length - 1) {
          const distance = this.levenshteinDistance(token, query);
          if (distance <= fuzzyThreshold && !matches.has(token)) {
            matches.add(token);
          }
        }
      });
    }
    
    return Array.from(matches);
  }

  findFuzzyMatches(query, threshold) {
    const matches = new Set();
    const tokenKeys = Array.from(this.tokenizedIndex.keys());
    
    tokenKeys.forEach(token => {
      if (token.length > 1 && query.length > 1) {
        const distance = this.levenshteinDistance(token, query);
        if (distance <= threshold + 1) {
          matches.add(token);
        }
      }
    });
    
    return Array.from(matches);
  }

  calculateSearchScore(item, query, matchedToken) {
    let score = 0;
    const sku = item.variant.sku ? item.variant.sku.toLowerCase() : '';
    const title = item.product.title ? item.product.title.toLowerCase() : '';
    const searchQuery = query.replace(/[-_\s]/g, '');
    const cleanSku = sku.replace(/[-_\s]/g, '');
    
    if (cleanSku === searchQuery) {
      score += 150;
    }
    
    if (sku === matchedToken) {
      score += 100;
    }
    
    if (sku.startsWith(query)) {
      score += 75;
    }
    
    if (sku.includes(query)) {
      score += 50;
    }
    
    if (query.length > 2 && sku.includes(query)) {
      const position = sku.indexOf(query);
      if (position === 0) {
        score += 25;
      } else if (position < sku.length / 2) {
        score += 15;
      }
    }
    
    if (matchedToken && sku.includes(matchedToken)) {
      score += 30;
    }
    
    if (title.includes(query)) {
      score += 20;
      if (title.startsWith(query)) {
        score += 10;
      }
    }
    
    if (item.product.product_type && 
        item.product.product_type.toLowerCase().includes(query)) {
      score += 15;
    }
    
    if (item.variant.title && 
        item.variant.title.toLowerCase().includes(query)) {
      score += 10;
    }
    
    return Math.round(score);
  }

  calculateProductScore(product, query) {
    let score = 0;
    const title = product.title ? product.title.toLowerCase() : '';
    
    if (title.includes(query)) {
      score += 20;
      if (title.startsWith(query)) {
        score += 10;
      }
    }
    
    if (product.available) {
      score += 5;
    }
    
    return score;
  }

  getMatchType(token, query) {
    if (token === query) return 'exact';
    if (token.startsWith(query)) return 'prefix';
    if (token.endsWith(query)) return 'suffix';
    if (token.includes(query)) return 'contains';
    if (this.levenshteinDistance(token, query) <= 2) return 'fuzzy';
    return 'partial';
  }

  updateSearchHistory(query, results) {
    this.searchHistory.push({
      query: query,
      timestamp: Date.now(),
      results: results.slice(0, 5).map(r => ({
        productId: r.product.id,
        variantId: r.variant.id,
        score: r.score
      }))
    });
    
    if (this.searchHistory.length > this.maxHistory) {
      this.searchHistory.shift();
    }
  }

  levenshteinDistance(str1, str2) {
    const m = str1.length;
    const n = str2.length;
    
    if (m === 0) return n;
    if (n === 0) return m;
    
    let prev = new Array(n + 1);
    let curr = new Array(n + 1);
    
    for (let j = 0; j <= n; j++) {
      prev[j] = j;
    }
    
    for (let i = 1; i <= m; i++) {
      curr[0] = i;
      for (let j = 1; j <= n; j++) {
        if (str1.charAt(i - 1) === str2.charAt(j - 1)) {
          curr[j] = prev[j - 1];
        } else {
          curr[j] = Math.min(prev[j - 1] + 1, curr[j - 1] + 1, prev[j] + 1);
        }
      }
      [prev, curr] = [curr, prev];
    }
    
    return prev[n];
  }

  clearCache() {
    this.cache.clear();
    console.log('🗑️ Search cache cleared');
  }

  getStats() {
    return {
      isInitialized: this.isInitialized,
      totalTokens: this.tokenizedIndex.size,
      totalProducts: this.productCache.size,
      cacheSize: this.cache.size,
      historySize: this.searchHistory.length,
      allProducts: this.allProducts.length,
      skuIndexSize: this.skuIndex.size
    };
  }
}

// ============================================
// Search Handler with UI Integration
// ============================================

class SmartSearchHandler {
  constructor() {
    console.log('🔍 Initializing Smart Search Handler...');
    
    this.searchInput = document.querySelector('#Search-In-Template');
    this.searchForm = document.querySelector('#SmartSearchForm');
    this.suggestionsContainer = document.querySelector('#search-suggestions');
    
    if (!this.suggestionsContainer && this.searchInput) {
      console.log('📝 Creating suggestions container...');
      this.suggestionsContainer = document.createElement('div');
      this.suggestionsContainer.id = 'search-suggestions';
      this.suggestionsContainer.className = 'search-suggestions-wrapper';
      this.searchInput.parentNode.appendChild(this.suggestionsContainer);
    }
    
    this.isOpen = false;
    this.currentQuery = '';
    this.selectedIndex = -1;
    this.results = [];
    this.debounceTimer = null;
    
    if (this.searchInput) {
      this.init();
    } else {
      console.error('❌ Search input #Search-In-Template not found!');
    }
  }
  
  init() {
    console.log('✅ Setting up event listeners...');
    
    this.searchInput.addEventListener('input', (e) => this.handleInput(e));
    this.searchInput.addEventListener('focus', () => this.handleFocus());
    this.searchInput.addEventListener('blur', (e) => this.handleBlur(e));
    this.searchInput.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', (e) => this.handleSubmit(e));
    }
    
    document.addEventListener('click', (e) => this.handleClickOutside(e));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeSuggestions();
      }
    });
    
    if (window.smartSearch) {
      window.smartSearch.initialize();
    }
    
    console.log('✅ Smart Search Handler ready!');
  }
  
  formatPrice(price) {
    if (!price && price !== 0) return '';
    const formatted = Number(price).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return `EGP ${formatted}`;
  }
  
  getProductImage(product) {
    try {
      if (product.featured_image) {
        if (typeof product.featured_image === 'string') {
          return product.featured_image;
        }
        if (typeof product.featured_image === 'object' && product.featured_image.src) {
          return product.featured_image.src;
        }
      }
      
      if (product.images && Array.isArray(product.images) && product.images.length > 0) {
        const firstImage = product.images[0];
        if (typeof firstImage === 'string') {
          return firstImage;
        }
        if (typeof firstImage === 'object' && firstImage.src) {
          return firstImage.src;
        }
      }
      
      if (product.image) {
        if (typeof product.image === 'string') {
          return product.image;
        }
        if (typeof product.image === 'object' && product.image.src) {
          return product.image.src;
        }
      }
      
      return '';
    } catch (error) {
      console.warn('Error getting product image:', error);
      return '';
    }
  }
  
  async handleInput(event) {
    const query = event.target.value.trim();
    this.currentQuery = query;
    
    clearTimeout(this.debounceTimer);
    
    if (query.length < 1) {
      this.closeSuggestions();
      return;
    }
    
    this.debounceTimer = setTimeout(async () => {
      try {
        this.showLoading();
        
        if (!window.smartSearch || !window.smartSearch.isInitialized) {
          await window.smartSearch.initialize();
        }
        
        const results = await window.smartSearch.search(query);
        this.results = results;
        
        if (results.length > 0) {
          this.displaySuggestions(results, query);
        } else {
          this.showNoResults(query);
        }
        
      } catch (error) {
        console.error('❌ Search error:', error);
        this.showError();
      }
    }, 150);
  }
  
  displaySuggestions(results, query) {
    if (!this.suggestionsContainer) return;
    
    const groupedResults = {
      exact: [],
      prefix: [],
      contains: [],
      fuzzy: [],
      partial: []
    };
    
    results.forEach(result => {
      if (groupedResults[result.matchType]) {
        groupedResults[result.matchType].push(result);
      } else {
        groupedResults.partial.push(result);
      }
    });
    
    let html = '<div class="suggestions-container">';
    
    const matchTypes = ['exact', 'prefix', 'contains', 'fuzzy', 'partial'];
    const matchLabels = {
      exact: '🎯 مطابق',
      prefix: '▶️ يبدأ بـ',
      contains: '🔍 يحتوي',
      fuzzy: '🔄 مشابه',
      partial: '📌 جزئي'
    };
    
    let shownCount = 0;
    const maxResults = 10;
    
    matchTypes.forEach(type => {
      const items = groupedResults[type] || [];
      if (items.length > 0 && shownCount < maxResults) {
        html += `<div class="suggestion-group"><div class="suggestion-group-title">${matchLabels[type]}</div>`;
        
        items.slice(0, maxResults - shownCount).forEach((result, index) => {
          html += this.createSuggestionItem(result, index, query);
          shownCount++;
        });
        
        html += '</div>';
      }
    });
    
    const stats = window.smartSearch ? window.smartSearch.getStats() : null;
    const totalProducts = stats ? stats.allProducts || stats.totalProducts : 0;
    
    html += `
      <div class="suggestion-footer">
        <span>عرض ${Math.min(results.length, maxResults)} من ${results.length} نتيجة ${totalProducts > 0 ? `| ${totalProducts} منتج` : ''}</span>
        <span class="suggestion-footer-action">⌨️ Enter للعرض الكامل</span>
      </div>
    `;
    
    html += '</div>';
    
    this.suggestionsContainer.innerHTML = html;
    this.openSuggestions();
    
    this.suggestionsContainer.querySelectorAll('.search-suggestion').forEach(el => {
      el.addEventListener('click', () => {
        const url = el.dataset.url;
        if (url) {
          window.location.href = url;
        }
      });
      
      el.addEventListener('mouseenter', () => {
        this.selectedIndex = parseInt(el.dataset.index);
        this.highlightSuggestion(this.selectedIndex);
      });
    });
  }
  
  createSuggestionItem(result, index, query) {
    const product = result.product;
    const variant = result.variant;
    const title = product.title || 'Product';
    const sku = variant.sku || '';
    
    const price = variant.price ? this.formatPrice(variant.price) : '';
    const score = result.score;
    
    const highlightedTitle = this.highlightText(title, query);
    const highlightedSku = this.highlightText(sku, query);
    const matchBadge = this.getMatchBadge(result.matchType);
    
    let imageUrl = this.getProductImage(product);
    
    if (typeof imageUrl !== 'string') {
      imageUrl = '';
    }
    
    if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('//') && !imageUrl.startsWith('https')) {
      imageUrl = '//' + imageUrl;
    }
    
    const scoreColor = score >= 80 ? '#059669' : score >= 60 ? '#d97706' : '#6b7280';
    const stockStatus = variant.available !== false ? ' متوفر' : ' غير متوفر';
    
    return `
      <div class="search-suggestion" 
           data-url="/products/${product.handle}?variant=${variant.id}"
           data-index="${index}">
        <div class="suggestion-image-wrapper">
          ${imageUrl ? `<img src="${imageUrl}" alt="${title}" class="suggestion-image" loading="lazy" onerror="this.style.display='none';this.parentElement.innerHTML='<div class=\\'suggestion-image-placeholder\\'>📦</div>'">` : '<div class="suggestion-image-placeholder">📦</div>'}
        </div>
        <div class="suggestion-content">
          <div class="suggestion-main">
            <span class="suggestion-title">${highlightedTitle}</span>
          </div>
          <div class="suggestion-meta">
            ${sku ? `<span class="suggestion-sku">${highlightedSku}</span>` : ''}
            ${price ? `<span class="suggestion-price">${price}</span>` : ''}
            <span class="suggestion-badge ${matchBadge.class}">${matchBadge.label}</span>
            <span class="suggestion-score" style="color: ${scoreColor}">${Math.round(score)}%</span>
            <span class="suggestion-stock">${stockStatus}</span>
          </div>
        </div>
      </div>
    `;
  }
  
  getMatchBadge(matchType) {
    const badges = {
      exact: { label: '🎯 مطابق', class: 'badge-exact' },
      prefix: { label: '▶️ يبدأ بـ', class: 'badge-prefix' },
      contains: { label: '🔍 يحتوي', class: 'badge-contains' },
      fuzzy: { label: '🔄 مشابه', class: 'badge-fuzzy' },
      partial: { label: '📌 جزئي', class: 'badge-partial' }
    };
    return badges[matchType] || badges.partial;
  }
  
  highlightText(text, query) {
    if (!text || !query) return text;
    try {
      const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return text.replace(regex, '<span class="suggestion-highlight">$1</span>');
    } catch (error) {
      return text;
    }
  }
  
  showLoading() {
    if (!this.suggestionsContainer) return;
    this.suggestionsContainer.innerHTML = `
      <div class="suggestions-container">
        <div class="suggestion-loading">
          <span class="loading-spinner"></span>
          <span>جاري البحث...</span>
        </div>
      </div>
    `;
    this.openSuggestions();
  }
  
  showNoResults(query) {
    if (!this.suggestionsContainer) return;
    this.suggestionsContainer.innerHTML = `
      <div class="suggestions-container">
        <div class="suggestion-no-results">
          <span class="no-results-icon">🔍</span>
          <p>لا توجد نتائج لـ <strong>"${query}"</strong></p>
          <p class="no-results-hint">💡 تأكد من الإملاء أو استخدم كلمات أقل</p>
        </div>
      </div>
    `;
    this.openSuggestions();
  }
  
  showError() {
    if (!this.suggestionsContainer) return;
    this.suggestionsContainer.innerHTML = `
      <div class="suggestions-container">
        <div class="suggestion-error">
          <span class="error-icon">⚠️</span>
          <p>حدث خطأ. حاول مرة أخرى.</p>
        </div>
      </div>
    `;
    this.openSuggestions();
  }
  
  openSuggestions() {
    if (!this.suggestionsContainer) return;
    this.isOpen = true;
    this.suggestionsContainer.classList.add('is-open');
    this.suggestionsContainer.style.display = 'block';
  }
  
  closeSuggestions() {
    if (!this.suggestionsContainer) return;
    this.isOpen = false;
    this.selectedIndex = -1;
    this.suggestionsContainer.classList.remove('is-open');
    this.suggestionsContainer.style.display = 'none';
  }
  
  handleFocus() {
    if (this.currentQuery.length >= 1 && this.results.length > 0) {
      this.openSuggestions();
    }
  }
  
  handleBlur(event) {
    setTimeout(() => {
      if (this.suggestionsContainer && 
          !this.suggestionsContainer.contains(document.activeElement)) {
        this.closeSuggestions();
      }
    }, 300);
  }
  
  handleKeydown(event) {
    if (!this.suggestionsContainer || !this.isOpen) return;
    const suggestions = this.suggestionsContainer.querySelectorAll('.search-suggestion');
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, suggestions.length - 1);
        this.highlightSuggestion(this.selectedIndex);
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.highlightSuggestion(this.selectedIndex);
        break;
        
      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0 && suggestions[this.selectedIndex]) {
          const url = suggestions[this.selectedIndex].dataset.url;
          if (url) {
            window.location.href = url;
          }
        } else if (this.results.length > 0 && this.searchForm) {
          this.searchForm.submit();
        }
        break;
        
      case 'Escape':
        this.closeSuggestions();
        break;
    }
  }
  
  highlightSuggestion(index) {
    if (!this.suggestionsContainer) return;
    const suggestions = this.suggestionsContainer.querySelectorAll('.search-suggestion');
    suggestions.forEach((el, i) => {
      if (i === index) {
        el.classList.add('is-selected');
        el.scrollIntoView({ block: 'nearest' });
      } else {
        el.classList.remove('is-selected');
      }
    });
  }
  
  handleSubmit(event) {
    const query = this.searchInput.value.trim();
    if (query.length < 1) {
      event.preventDefault();
      this.searchInput.focus();
      return;
    }
  }
  
  handleClickOutside(event) {
    if (this.suggestionsContainer && 
        this.searchInput &&
        !this.suggestionsContainer.contains(event.target) &&
        !this.searchInput.contains(event.target)) {
      this.closeSuggestions();
    }
  }
}

// ============================================
// Initialize
// ============================================

window.smartSearch = new SmartSearchAI();

function initHandler() {
  if (!window.searchHandler) {
    try {
      window.searchHandler = new SmartSearchHandler();
    } catch (error) {
      console.error('❌ Failed to init handler:', error);
      setTimeout(initHandler, 1000);
    }
  }
}

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  initHandler();
} else {
  document.addEventListener('DOMContentLoaded', initHandler);
}

setTimeout(() => {
  if (!window.searchHandler) {
    initHandler();
  }
}, 1000);

console.log('✅ Smart Search AI ready!');